import { useState, type ReactNode } from 'react'
import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/** What an operator can do to a signature field — the legacy menu, item for item. */
export type SignatureAction = 'apply' | 'remove' | 'signAs' | 'removeAs' | 'auditLog' | 'allowedSignatories'

interface SignatureFieldMenuProps {
  /** The field's name, for the menu button's accessible name. */
  fieldLabel: string
  signed: boolean
  /** Why nobody can sign right now (locked, gate), or null. Applies to Sign as too. */
  signBlocked: string | null
  /**
   * Why the SIGNED-IN user cannot apply it, or null. Wider than `signBlocked`:
   * someone outside the allowed groups cannot apply it themselves, but can
   * still hand it to a colleague through Sign as.
   */
  applyBlocked: string | null
  /** Why removal is unavailable right now (e.g. the form is completed). */
  removeBlocked: string | null
  /** Holds signatures.unsign. Without it the remove items are HIDDEN, not disabled (z8nrz7dt40 AC4). */
  canUnsign: boolean
  /** Sign as / Remove as need the tenant roster — off in the builder preview. */
  offerSignAs: boolean
  /** Audit log / allowed signatories read the server — hidden in the builder preview. */
  showRecordItems: boolean
  onAction: (action: SignatureAction) => void
  /** The signature box. Right-clicking it opens the same menu, as the legacy screen did. */
  children: ReactNode
}

interface Item {
  action: SignatureAction
  label: string
  disabledReason: string | null
}

/**
 * The signature field's action menu (z8nrz7dt3z / dt40 / dt45), reproducing
 * the legacy platform's right-click menu: Apply Signature, Remove Signature,
 * Sign as:, Remove as:, View Audit Log, View Allowed Signatories.
 *
 * Right-click on the field opens it, as it did on the legacy screen; a visible
 * menu button opens it too, because a right-click menu is invisible to anyone
 * who does not already know it is there (and to touch screens).
 */
export function SignatureFieldMenu({
  fieldLabel,
  signed,
  signBlocked,
  applyBlocked,
  removeBlocked,
  canUnsign,
  offerSignAs,
  showRecordItems,
  onAction,
  children,
}: SignatureFieldMenuProps) {
  const [open, setOpen] = useState(false)

  const alreadySigned = signed ? 'Already signed — remove the signature first' : null
  const notSigned = signed ? null : 'Not signed'
  const signingItems: Item[] = [
    { action: 'apply', label: 'Apply Signature', disabledReason: alreadySigned ?? applyBlocked },
    ...(canUnsign
      ? [{ action: 'remove' as const, label: 'Remove Signature', disabledReason: notSigned ?? removeBlocked }]
      : []),
    ...(offerSignAs
      ? [{ action: 'signAs' as const, label: 'Sign as…', disabledReason: alreadySigned ?? signBlocked }]
      : []),
    ...(canUnsign && offerSignAs
      ? [{ action: 'removeAs' as const, label: 'Remove as…', disabledReason: notSigned ?? removeBlocked }]
      : []),
  ]
  const recordItems: Item[] = showRecordItems
    ? [
        { action: 'auditLog', label: 'View Audit Log', disabledReason: null },
        { action: 'allowedSignatories', label: 'View Allowed Signatories', disabledReason: null },
      ]
    : []

  const choose = (item: Item) => {
    if (item.disabledReason) return
    setOpen(false)
    onAction(item.action)
  }

  const renderItem = (item: Item) => (
    <button
      key={item.action}
      type='button'
      role='menuitem'
      aria-disabled={!!item.disabledReason}
      title={item.disabledReason ?? undefined}
      onClick={() => choose(item)}
      className={cn(
        'flex w-full flex-col rounded-sm px-2 py-1.5 text-left text-sm',
        item.disabledReason ? 'cursor-not-allowed text-muted-foreground/60' : 'hover:bg-accent',
      )}
    >
      {item.label}
      {item.disabledReason && <span className='text-[11px] leading-tight'>{item.disabledReason}</span>}
    </button>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          className='relative'
          onContextMenu={e => {
            e.preventDefault()
            setOpen(true)
          }}
        >
          {children}
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='absolute right-2 top-2 h-7 w-7 text-blue-700 hover:bg-blue-100'
              aria-label={`Signature options: ${fieldLabel}`}
            >
              <MoreVertical className='h-4 w-4' />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent align='end' className='w-64 p-1'>
        <div role='menu' aria-label={`Signature options: ${fieldLabel}`}>
          {signingItems.map(renderItem)}
          {recordItems.length > 0 && <div role='separator' className='my-1 h-px bg-border' />}
          {recordItems.map(renderItem)}
        </div>
      </PopoverContent>
    </Popover>
  )
}
